import { useEffect } from 'react';
import { NavLink, Navigate, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { driverApi, getHostAudience } from '@l2u/web-shared';
import { useCompanyAuth } from '../lib/auth/company.tsx';
import { useBranding, useConfirm, useEntityLabels } from '@l2u/web-shared';
import { CenteredSpinner } from '@l2u/web-shared';
import { CompanyStatusBadge } from '@l2u/web-shared';
import { ProgressBar } from '@l2u/web-shared';
import { NavIcon } from '@l2u/web-shared';
import { BottomTabBar, MobileAppBar } from '@l2u/web-shared';
import { BrandMark, PRODUCT_NAME, displayBrandName, initials } from '@l2u/web-shared';
import type { Driver } from '@l2u/web-shared';
import {
  driverAppBarTitle,
  driverOnboardingHeading,
  driverQuestionnaireBadge,
  driverTabs,
} from '../lib/driverNav.ts';
import { driverPath, familyBase } from '../lib/audienceRoutes.ts';
import { shellRedirectForAny } from '../lib/lockedRedirect.ts';
import { useQuestionnaireBadgeCount } from '../lib/useVendorActionCount.ts';
import { driverNavCountsQueryOptions } from '../lib/driverNavCounts.ts';
import { NavCountBadge } from '../components/actionIndicators.tsx';

type DriverWithCompany = Driver & {
  company: { id: string; company_name: string; contact_name: string } | null;
};

export function DriverShell() {
  const { session, loading, signOut } = useCompanyAuth();
  const confirm = useConfirm();
  const labels = useEntityLabels();
  const heading = driverOnboardingHeading(labels.driver);

  // There is no password: signing out costs a round trip through the inbox, so
  // it must not be a one-tap accident.
  async function confirmSignOut() {
    const ok = await confirm({
      title: 'Sign out?',
      body: 'You will need a new sign-in link emailed to you to get back in.',
      confirmLabel: 'Sign out',
    });
    if (ok) await signOut();
  }
  const { branding } = useBranding();
  const location = useLocation();
  // '' on the driver host, '/d' on the shared one — where App mounted the pages.
  const driverBase = familyBase('driver', getHostAudience());
  const navigate = useNavigate();
  const brandName = displayBrandName(branding.companyName);

  const { data, isLoading, error } = useQuery({
    queryKey: ['driver-me'],
    queryFn: ({ signal }) =>
      driverApi.get<{ driver: DriverWithCompany }>('/api/driver/me', { signal }),
    enabled: !!session,
  });
  // The Questionnaire badge: things this driver has to action, counted exactly
  // like the company portal's, by the API (GET /api/driver/me/action-count, in
  // the background — /api/driver/me itself never waits on it). The lists are
  // fetched here only if that endpoint fails. Null until known —
  // driverQuestionnaireBadge then holds the server's pending_questionnaire_count.
  const badge = useQuestionnaireBadgeCount('driver', !!session);
  const actionCount = badge.count;
  // Training, Library and File notes: one aggregated request
  // (GET /api/driver/me/nav-counts), in the background — nothing waits on it, and a failure just shows no badges.
  const navCounts = useQuery({ ...driverNavCountsQueryOptions(), enabled: !!session });

  // Off all the shell's own queries, whichever lands first — see CompanyShell.
  const redirectTo = shellRedirectForAny([error, navCounts.error, badge.error], 'driver');
  useEffect(() => {
    // Every 401/403 used to land on /expired, including `account_locked` —
    // a 403 — which told a locked driver their LINK had expired and offered to
    // email a fresh one straight back into the lock. The lock is checked
    // first; see lib/lockedRedirect.ts.
    // /d/expired, not /c/expired: the company page is copy written for the
    // driver's EMPLOYER, telling them to contact an administrator they have
    // never met.
    if (redirectTo) {
      void signOut();
      navigate(driverPath(redirectTo), { replace: true });
    }
  }, [redirectTo, signOut, navigate]);

  if (loading) return <CenteredSpinner />;
  // No session at all is someone who has not signed in yet — most often a
  // visitor who typed the bare hostname — so they go to the sign-in form. The
  // "your link has expired" page is only for a session the API actually
  // REJECTED, which the 401 effect above handles; showing it to a first-time
  // visitor told them about a link they never had.
  if (!session) {
    return <Navigate to={driverPath('/login')} state={{ from: location }} replace />;
  }
  // Nothing else waits for /api/driver/me: the chrome renders with
  // placeholders and <Outlet/> mounts at once so the page's own queries run in
  // parallel with it — see CompanyShell for the full reasoning. The one gate
  // kept is the frame between a refused shell query and the effect's redirect.
  if (redirectTo) return <CenteredSpinner />;
  const driver = data?.driver;
  const driverLoading = !driver && isLoading;
  // The action count REPLACES pending_questionnaire_count (never added to it),
  // and the server count holds until both lists have loaded — see
  // driverQuestionnaireBadge.
  const tabs = driverTabs(driverBase, {
    questionnaire: driverQuestionnaireBadge(actionCount, driver?.pending_questionnaire_count),
    // Undefined while loading or failed: no bubble rather than a guess.
    training: navCounts.data?.counts.training,
    library: navCounts.data?.counts.library,
    'file-notes': navCounts.data?.counts.file_notes,
  });

  return (
    // Below `md` this is a viewport-height column so <main> owns the scroll and
    // the tab bar can sit at its foot without `position: fixed`. At `md`+ height
    // reverts to the growing min-h-screen column it has always been. `h-dvh`
    // rather than `h-screen`, matching AdminShell: 100vh is wrong on mobile
    // Safari, where collapsing the URL bar changes the real viewport.
    // `driver-portal` is not a style of its own: it opts this subtree into the
    // phone touch-target and 16px-input rules in index.css.
    <div className="driver-portal flex h-dvh flex-col overflow-hidden bg-slate-50 md:h-auto md:min-h-screen md:overflow-visible">
      {/* Phone chrome. The app bar carries the page title the desktop header
          carries at `md`+; `showBack` is never passed: the one detail route, a
          file note, is opened from an email as often as from its list, so a
          history-back chevron could leave the portal — the page carries its own
          link back to the list instead. */}
      <MobileAppBar
        hideAt="md"
        // Still passed, now as the bar's accessible name rather than its visible
        // text — see the `brand` slot below.
        title={driverAppBarTitle(location.pathname, driverBase) ?? heading}
        brand={
          // The PRODUCT's lockup, deliberately, where the desktop header a few
          // lines down shows the TENANT's logo and "<Driver> Onboarding". On a
          // 390px bar there is room for one identity, and the tenant is already
          // named on the line underneath.
          <>
            <BrandMark size={28} />
            <div className="min-w-0 flex-1">
              <div className="truncate text-[15px] font-semibold leading-[1.25] tracking-[-0.022em] text-slate-900">
                {PRODUCT_NAME}
              </div>
              {driver && (
                <div className="truncate text-[11px] leading-[1.35] text-slate-500">
                  {driver.name}
                  {driver.company?.company_name ? ` · ${driver.company.company_name}` : ''}
                </div>
              )}
            </div>
          </>
        }
        {...(driver?.name ? { userName: driver.name } : {})}
        action={
          // The admin app hides Sign out behind its More tab. Five tabs and no
          // More leaves the app bar as the only home for it here. The confirm
          // stays — signing out of a passwordless portal means waiting for
          // another emailed link, and a thumb makes the accident more likely,
          // not less.
          <button
            type="button"
            onClick={() => void confirmSignOut()}
            aria-label="Sign out"
            className="flex h-11 w-11 shrink-0 items-center justify-center text-slate-500"
          >
            <NavIcon name="log-out" size={20} />
          </button>
        }
      />
      {driverLoading && (
        <div aria-hidden="true" className="shrink-0 border-b bg-white px-4 pb-3 pt-2 md:hidden">
          <div className="h-8 animate-pulse rounded bg-slate-100" />
        </div>
      )}
      {driver && (
        // The driver's whole reason for being here, so it survives the phone. A
        // separate element rather than one shared with the desktop copy below:
        // the container and padding differ, and sharing would mean two sets of
        // responsive overrides on one node.
        <div className="shrink-0 border-b bg-white px-4 pb-3 pt-2 md:hidden">
          <ProgressBar value={driver.progress} label="Progress" />
        </div>
      )}

      <header className="hidden border-b bg-white md:block" style={{ borderColor: '#E2E8F0' }}>
        {/* The tenant's logo on a strip of its own, at its natural width. It used
            to sit in a 36px square tile, which shrank a wide wordmark to 20px. */}
        <div className="border-b border-slate-100">
          <div className="mx-auto flex max-w-5xl items-center justify-center px-6 py-3">
            {branding.logoUrl ? (
              <img src={branding.logoUrl} alt={brandName} className="h-9 w-auto max-w-xs" />
            ) : (
              <span
                className="text-lg font-bold tracking-tight"
                style={{ color: 'var(--brand-primary)' }}
              >
                {brandName}
              </span>
            )}
          </div>
        </div>
        <div className="mx-auto flex max-w-5xl flex-wrap items-center justify-between gap-3 px-6 py-4">
          <div className="flex items-center gap-3">
            {/* The driver's initials, styled and derived exactly like the phone
                app bar's avatar so the two never disagree. */}
            {driver ? (
              <div
                className="font-mono-eyebrow flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-brand-light text-[13px] font-extrabold tracking-[0.02em] text-brand-active"
                title={driver.name}
                aria-hidden="true"
              >
                {initials(driver.name)}
              </div>
            ) : driverLoading ? (
              <div
                aria-hidden="true"
                className="h-10 w-10 shrink-0 animate-pulse rounded-full bg-slate-100"
              />
            ) : null}
            <div>
              <div
                className="text-[15px] font-bold leading-tight tracking-tight"
                style={{ color: 'var(--brand-primary)' }}
              >
                {heading}
              </div>
              {driver && (
                <div className="text-xs text-slate-500">
                  {driver.name}
                  {driver.company?.company_name ? ` · ${driver.company.company_name}` : ''}
                </div>
              )}
              {driverLoading && (
                <div
                  aria-hidden="true"
                  data-testid="driver-name-skeleton"
                  className="mt-1 h-3 w-40 animate-pulse rounded bg-slate-100"
                />
              )}
            </div>
          </div>
          <div className="flex items-center gap-3">
            {/* Desktop only, and deliberately given no phone home: the app bar
                has no room, and the status is already carried by the progress
                bar above and the per-question banners inside. */}
            {driver && <CompanyStatusBadge status={driver.status} audience="vendor" />}
            {/* Confirmed, because signing out of a passwordless portal means
                waiting for another emailed link — see the same guard on the
                company shell and on the app bar's action above. */}
            <button
              type="button"
              onClick={() => void confirmSignOut()}
              className="rounded-md border border-slate-200 bg-white px-3 py-1.5 text-xs text-slate-600 hover:bg-slate-50"
            >
              Sign out
            </button>
          </div>
        </div>
        {driver && (
          <div className="mx-auto max-w-5xl px-6 pb-4">
            <ProgressBar value={driver.progress} label="Progress" />
          </div>
        )}
        {driverLoading && (
          <div aria-hidden="true" className="mx-auto max-w-5xl px-6 pb-4">
            <div className="h-8 animate-pulse rounded bg-slate-100" />
          </div>
        )}
        {/* The desktop tab row. It had no overflow handling at all, which is why
            the phone gets a bottom bar rather than this squeezed down. */}
        <nav className="mx-auto flex max-w-5xl gap-1 px-6">
          {tabs.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              className={({ isActive }) =>
                `border-b-2 px-3 py-2.5 text-sm transition ${
                  isActive ? 'font-semibold' : 'border-transparent font-medium'
                }`
              }
              style={({ isActive }) =>
                isActive
                  ? { borderColor: 'var(--brand-primary)', color: 'var(--brand-primary)' }
                  : { color: '#475569' }
              }
            >
              {item.label}
              <NavCountBadge
                count={item.badge ?? 0}
                inline
                {...(item.badgeLabel ? { label: item.badgeLabel } : {})}
              />
            </NavLink>
          ))}
        </nav>
      </header>

      {/* 24px of side padding costs 12% of a 390px screen, so the phone gets 16.
          `overflow-y-auto` makes this the scroll container below `md`, which is
          what lets the tab bar sit at the column's foot un-fixed. `min-h-0` is
          what makes that work at all: a flex item defaults to min-height:auto and
          refuses to shrink below its content, so without it the column grows and
          pushes the tab bar off-screen instead of scrolling. */}
      <main className="mx-auto min-h-0 w-full max-w-5xl flex-1 overflow-y-auto px-4 py-5 md:min-h-[auto] md:overflow-visible md:px-6 md:py-8">
        <Outlet />
      </main>

      {/* Every count rides on the tab itself (driverTabs), not on a `badges`
          record — a driver has no review queue to count. */}
      <BottomTabBar hideAt="md" tabs={tabs} />
    </div>
  );
}
